import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { useContext } from 'react';

import { UserContext } from '~/components/providers/UserProvider';
import { reportEvent } from '~/domains/analytics';
import { AUTH_STATUSES, UNIFIED_LOGIN_ERRORS } from '~/domains/auth/consts';
import useEORedirectUrl from '~/domains/auth/hooks/useEORedirectUrl';
import { useGetBranding } from '~/domains/auth/hooks/useGetBranding';
import { useRecaptcha } from '~/domains/auth/hooks/useRecaptcha';
import { Routes } from '~/domains/common/constants/routes';
import { useShowSplashNotification } from '~/domains/common/hooks/useShowSplashNotification';
import type { LogInInput } from '~/services/identity/unauthenticated/identity.service.api';
import { logIn } from '~/services/identity/unauthenticated/identity.service.api';
import type { APIAuthUserData } from '~/services/users/types';
import { me } from '~/services/users/users.service.api';

import { setTokens } from '../utils/setTokens';
import { removeTokens } from '../utils/removeTokens';
import {
  resolveAuthErrorMessage,
  type AuthErrorResponseBody,
  type AuthMutationError,
} from '../utils/resolveAuthErrorMessage';

const useLogin = ({
  onSuccess,
  onMultiFactorRequired,
}: {
  onSuccess?: (user: APIAuthUserData, redirectUrl?: string) => void;
  onMultiFactorRequired?: (verificationToken: string, email: string, canResendIn?: number) => void;
}) => {
  const { setUser } = useContext(UserContext);
  const router = useRouter();
  const { getRecaptchaToken } = useRecaptcha();
  const { t } = useTranslation('auth');
  const { destination, platform } = useGetBranding();
  const { getEORedirectUrl } = useEORedirectUrl();
  const { showSplashNotification } = useShowSplashNotification();

  const { mutate: handleLogin, isLoading: isLoggingIn } = useMutation({
    mutationKey: ['logIn'],
    mutationFn: async (logInPayload: LogInInput) => {
      const token = await getRecaptchaToken();

      const logInData = await logIn({
        ...logInPayload,
        token,
        destination,
        platform,
      });

      if (logInData.status === AUTH_STATUSES.MULTI_FACTOR_REQUIRED) {
        return {
          mfaRequired: true as const,
          verificationToken: logInData.verificationToken,
          email: logInPayload.email,
          canResendIn: logInData.canResendIn,
        };
      }

      setTokens(logInData);

      const user = await me();
      const singleUseToken = logInData.singleUseToken?.token ?? null;

      return {
        mfaRequired: false as const,
        user,
        userId: logInData.id,
        singleUseToken,
      };
    },
    onSuccess: async (result) => {
      if (result.mfaRequired) {
        onMultiFactorRequired?.(result.verificationToken, result.email, result.canResendIn);
        return;
      }

      const { singleUseToken, user, userId } = result;
      const userData = user?.data;

      reportEvent('User > Login', {
        userId,
        brand: destination,
        platform,
        ...userData,
      });

      const eoRedirectUrl = getEORedirectUrl(destination, platform, singleUseToken);

      /**
       * This is a temporary solution to avoid setting the user in the context and storage for EO Brand login
       * (this triggers a lot of other API calls and events)
       * EO Brand login only requires setting access & refresh token cookies
       *
       * @TODO Create a wholer separate EO AUTH sandbox to minimise complexity
       */
      if (!destination) {
        window.sessionStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
      }

      if (onSuccess) onSuccess(userData, eoRedirectUrl);
    },
    onError: (error: AuthMutationError) => {
      removeTokens();

      // Non-Axios throws reach onError too (e.g. a sessionStorage quota error from me())
      const response = axios.isAxiosError<AuthErrorResponseBody>(error)
        ? error.response
        : undefined;
      const errorMessage = response?.data?.message;
      const showSupportCTA = response?.data?.showSupportCTA;
      const errorType = response?.data?.error;
      if (UNIFIED_LOGIN_ERRORS.includes(errorType)) {
        router.push(
          {
            pathname: '/account/reset-password',
            query: {
              email: errorMessage,
              automatedSend: 1,
              brand: destination,
              platform,
            },
          },
          '/account/reset-password'
        );
      } else {
        showSplashNotification({
          title: t('signIn.errors.title'),
          message: resolveAuthErrorMessage(error, t('signIn.errors.messages.wrongCredentials'), [
            'FRAUDULENT_ACTIVITY_DETECTED_ERROR',
          ]),
          color: 'red',
          autoClose: 5000,
          showSupportCTA,
        });
        // Redirect back to login page on error if the user is not on already on the login page
        if (router.pathname !== Routes.signIn()) {
          router.replace(
            Routes.signIn({
              redirectTo: `${router.query.redirectTo}`,
              brand: destination,
              platform,
            })
          );
        }
      }
    },
  });

  return { handleLogin, isLoggingIn };
};

export default useLogin;
